import React, { useState, useEffect } from "react";
import apiClient from "../api/client";

// ── Colours ───────────────────────────────────────────────────────────────────
const NAVY  = "#1B3A5C";
const TEAL  = "#0D7377";

// ── Sub-tab list ──────────────────────────────────────────────────────────────
const TABS = [
  { id: "invoices",     label: "Invoices" },
  { id: "forecasting",  label: "Forecasting" },
  { id: "budgeting",    label: "Budgeting" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(val, decimals = 0) {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${Number(val).toFixed(2)}%`;
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin"
        style={{ borderTopColor: TEAL }} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
      {message}
    </div>
  );
}

// ── Apply button ──────────────────────────────────────────────────────────────
function ApplyButton({ onClick, dirty }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors mt-4"
      style={{ backgroundColor: dirty ? TEAL : "#94a3b8", cursor: dirty ? "pointer" : "default" }}
    >
      Apply
    </button>
  );
}

// ── Global filter bar ─────────────────────────────────────────────────────────
// Holds pending state locally, only fires onChange when Apply is clicked
function FilterBar({ filters, onChange }) {
  const [pending, setPending] = useState(filters);
  const dirty = JSON.stringify(pending) !== JSON.stringify(filters);

  // Sync if parent resets
  useEffect(() => { setPending(filters); }, [filters]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">From</label>
            <input
              type="month"
              value={pending.startMonth}
              onChange={e => setPending(p => ({ ...p, startMonth: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="mt-4">→</div>
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">To</label>
            <input
              type="month"
              value={pending.endMonth}
              onChange={e => setPending(p => ({ ...p, endMonth: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <div className="flex items-end gap-2">
          <ApplyButton onClick={() => onChange(pending)} dirty={dirty} />
          {(filters.startMonth || filters.endMonth) && (
            <button
              onClick={() => { const r = { startMonth: "", endMonth: "" }; setPending(r); onChange(r); }}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-700 mt-4"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 1. Invoices ───────────────────────────────────────────────────────────────
function Invoices({ filters }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [editing, setEditing] = useState({});
  const [saving,  setSaving]  = useState({});

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.startMonth) params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)   params.end_month   = filters.endMonth   + "-01";

    apiClient.get("/api/finance/invoices", { params })
      .then(res => { setData(res.data); setError(null); })
      .catch(() => setError("Failed to load invoices. The invoice tables may not exist yet — run the full pipeline first."))
      .finally(() => setLoading(false));
  }, [filters]);

  async function saveInvoice(row, value) {
    const month = (row.month?.value || row.month)?.slice(0, 10);
    const key   = `${row.contract_name}|${month}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await apiClient.post(
        `/api/finance/invoices/${encodeURIComponent(row.contract_name)}/${month}`,
        { actual_invoice_amount: parseFloat(value) }
      );
      setData(d => d.map(r =>
        (r.contract_name === row.contract_name &&
         (r.month?.value || r.month)?.slice(0,10) === month)
          ? { ...r, actual_invoice_amount: parseFloat(value) }
          : r
      ));
      setEditing(e => { const n = { ...e }; delete n[key]; return n; });
    } catch {
      alert("Failed to save invoice amount. Please try again.");
    } finally {
      setSaving(s => { const n = { ...s }; delete n[key]; return n; });
    }
  }

  if (loading) return <LoadingSpinner />;
  if (error)   return <ErrorBox message={error} />;
  if (!data.length) return <p className="text-gray-400 text-sm">No invoice data found.</p>;

  function varianceColor(pct) {
    const p = Math.abs(Number(pct) || 0);
    if (p <= 5)  return { backgroundColor: "#F0FDF4", color: "#166534" };
    if (p <= 15) return { backgroundColor: "#FFFBEB", color: "#92400E" };
    return { backgroundColor: "#FEF2F2", color: "#991B1B" };
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {["Month","Contract","Currency","Calculated Cost","Actual Invoice","Variance","Variance %","MG Applied"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const month    = (row.month?.value || row.month)?.slice(0, 7);
              const fullDate = (row.month?.value || row.month)?.slice(0, 10);
              const key      = `${row.contract_name}|${fullDate}`;
              const isEditing = key in editing;

              return (
                <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-2.5">{month}</td>
                  <td className="px-4 py-2.5 font-medium">{row.contract_name}</td>
                  <td className="px-4 py-2.5">{row.payment_currency}</td>
                  <td className="px-4 py-2.5">{fmt(row.calculated_cost_post_mg, 2)}</td>
                  <td className="px-4 py-2.5">
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={editing[key]}
                          disabled={!!saving[key]}
                          className="w-28 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                          onKeyDown={e => {
                            if (e.key === "Enter")  saveInvoice(row, e.target.value);
                            if (e.key === "Escape") setEditing(ed => { const n={...ed}; delete n[key]; return n; });
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => setEditing(ed => { const n={...ed}; delete n[key]; return n; })}
                          className="text-gray-400 hover:text-gray-600 text-xs"
                        >✕</button>
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer hover:text-teal-600 transition-colors"
                        onClick={() => setEditing(e => ({ ...e, [key]: row.actual_invoice_amount || "" }))}
                        title="Click to enter invoice amount"
                      >
                        {row.actual_invoice_amount != null
                          ? fmt(row.actual_invoice_amount, 2)
                          : <span className="text-gray-300 italic text-xs">click to enter</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">{row.variance != null ? fmt(row.variance, 2) : "—"}</td>
                  <td className="px-4 py-2.5">
                    {row.variance_pct != null
                      ? <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={varianceColor(row.variance_pct)}>
                          {fmtPct(row.variance_pct)}
                        </span>
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.mg_applied_this_month
                      ? <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>Yes</span>
                      : <span className="text-xs text-gray-400">No</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 2. Placeholder tabs (Forecasting, Budgeting) ─────────────────────────────
function ComingSoon({ label }) {
  return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-400 text-lg">{label} — coming soon</p>
    </div>
  );
}

// ── Main Finance page ─────────────────────────────────────────────────────────
export default function Finance() {
  const [activeTab,   setActiveTab]   = useState("invoices");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["invoices"]));
  const [filters,     setFilters]     = useState({
    startMonth: "",
    endMonth:   "",
  });

  function handleTabClick(tabId) {
    setActiveTab(tabId);
    setVisitedTabs(prev => new Set([...prev, tabId]));
  }

  const TAB_COMPONENTS = {
    invoices:    Invoices,
    forecasting: () => <ComingSoon label="Forecasting" />,
    budgeting:   () => <ComingSoon label="Budgeting" />,
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Finance</h1>
        <p className="text-sm text-gray-500 mt-1">Invoice reconciliation and financial reporting</p>
      </div>

      <FilterBar filters={filters} onChange={setFilters} />

      <div className="flex gap-1 mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={activeTab === tab.id
              ? { backgroundColor: TEAL, color: "white" }
              : { color: "#6b7280" }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {TABS.map(tab => {
        const Component = TAB_COMPONENTS[tab.id];
        if (!visitedTabs.has(tab.id)) return null;
        return (
          <div key={tab.id} style={{ display: activeTab === tab.id ? "block" : "none" }}>
            <Component filters={filters} />
          </div>
        );
      })}
    </div>
  );
}
